import React, { useState, useEffect } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { notification, Spin, Modal, Button as AntButton, Table, Input, Tag, Dropdown, Space, Select } from 'antd';
import { FaArrowLeft, FaTrash } from "react-icons/fa";
import { DownOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import { Dispatch } from '@reduxjs/toolkit';

import style from './Form.module.css';
import { FormInput, FormSelect, FormTextArea } from '../components/Basic/FormInput';
import Button from '../components/Basic/button';
import * as ACTIONS from '../store/slices/createCampaign';
import { getDecodedToken } from '../utils/auths';


// Types
interface CampaignForm {
  Campaign: string;
  StartDate: string;
  EndDate: string;
  WinnerAnnouncement: string;
  WinnerBranchExcluded: string;
  NoOfWinners: number;
  WinnerAnnouncementSelection: string;
  CustomerExcluded: string;
  BlackListedCustomerExcluded: string;
  Prize: string;
  Amount?: string;
  Asset?: string;
  Description: string;
  fileId: string;
  reportType: string;
  isMockCampaign?: string;
}

interface FieldDataType {
  fieldId: number;
  fieldName: string;
  fieldType: string;
  fieldOperation: string;
  fieldOperationLabel: string;
  fieldValue: string;
  fieldValuePlaceholder: string;
  fieldRangeValue: {
    value1: string;
    value2: string;
  };
  fieldArrValue: string[];
}

interface PersonalProps {
  Back: () => void;
  createCampaign_Red: any;
  createCampaign: (campaignData: any) => Promise<any>;
  getByID: (campaignId: string) => Promise<any>;
  updateCampaign: (campaignData: any) => Promise<any>;
  getField: () => Promise<any>;
  getUnapprovedFiles: () => Promise<any>;
  getAllBranches: (institutionId: number) => Promise<any>;
  getAllBins: () => Promise<any>;
  getProductsByBin: (binId: number) => Promise<any>;
  getReportTypes: () => Promise<any>;
  currentCampaignId?: string | null;
}

interface WinnerRow {
  key: string;
  period: string;
  startDate: Date;
  endDate: Date;
  winnerNumber: string;
  customerExcluded: string;
  winnerAnnouncementSelection: string;
  prizeType: string;
  prizeValue: string;
}




// Validation Schema
const validationSchema = yup.object().shape({
  Campaign: yup.string().required('Campaign Name is required'),
  StartDate: yup.string().nullable().required('Start Date is required'),
  EndDate: yup.string().nullable().required('End Date is required')
    .test('end-after-start', 'End Date must be after Start Date', function (endDate) {
      const { StartDate } = this.parent;
      if (!StartDate || !endDate) return true;
      return new Date(endDate) >= new Date(StartDate);
    }),
  WinnerAnnouncement: yup.string().required('Winner Announcement is required'),
  WinnerBranchExcluded: yup.string().required('Winner Branch Excluded is required'),
  NoOfWinners: yup.number().transform((value, originalValue) => {
    return originalValue === '' ? undefined : value;
  }).positive('Must be a positive number').integer('Must be a whole number').max(100, 'Maximum 100 winners allowed').required('No of Winners is required'),
  CustomerExcluded: yup.string().required('Customer Excluded is required'),
  BlackListedCustomerExcluded: yup.string().required('Black Listed Customer Excluded is required'),
  WinnerAnnouncementSelection: yup.string().required('Winner Announcement Selection is required'),
  Prize: yup.string().required('Prize Type is required'),
  Amount: yup
    .string()
    .when('Prize', {
      is: 'Prize',
      then: (schema) => schema.required('Amount is required'),
      otherwise: (schema) => schema.optional(),
    }),
  Asset: yup
    .string()
    .when('Prize', {
      is: 'Asset',
      then: (schema) => schema.required('Asset is required'),
      otherwise: (schema) => schema.optional(),
    }),
  fileId: yup.string().required('File is required'),
  Description: yup.string().required('Description is required'),
  reportType: yup.string().required('Campaign Type is required'),
});

// Constants
const OPERATIONS = [
  { label: 'Less Than', value: '1', operator: '<', applicableType: ['number', 'date'] },
  { label: 'Less Than or Equal', value: '2', operator: '<=', applicableType: ['number', 'date'] },
  { label: 'Greater Than', value: '3', operator: '>', applicableType: ['number', 'date'] },
  { label: 'Greater Than or Equal', value: '4', operator: '>=', applicableType: ['number', 'date'] },
  { label: 'Equal To', value: '5', operator: '=', applicableType: ['number', 'text', 'date', 'string'] },
  { label: 'In List', value: '6', operator: 'in', applicableType: ['number', 'text', 'date', 'string'] },
  { label: 'Not In List', value: '7', operator: 'not in', applicableType: ['number', 'text', 'date', 'string'] },
  { label: 'Between Range', value: '8', operator: 'between', applicableType: ['number', 'date'] },
  { label: 'Like', value: '9', operator: 'like', applicableType: ['text', 'string'] },
];

const WINNER_ANNOUNCEMENT_OPTIONS = [
  // { label: 'Maximum Spent Amount', value: 'MAX_SPENT' },
  // { label: 'Maximum Transaction', value: 'MAX_VALUE' },  
  { label: 'Lucky Draw', value: 'LUCKY' },
];

const WINNER_TYPE_OPTIONS = [
  { label: 'End Of Campaign', value: 'End Of Campaign' },
  { label: 'Weekly Winner', value: 'Weekly Winner' },
  { label: 'Monthly Winner', value: 'Monthly Winner' },
];

const YES_NO_OPTIONS = [
  { label: 'Yes', value: 'Yes' },
  { label: 'No', value: 'No' },
];

const PRIZE_TYPE_OPTIONS = [
  { label: 'Asset', value: 'Asset' },
  { label: 'Prize', value: 'Prize' },
];

const PersonalInfoForm: React.FC<PersonalProps> = ({ 
  Back, 
  createCampaign_Red, 
  createCampaign, 
  getByID, 
  updateCampaign, 
  currentCampaignId, 
  getField,
  getUnapprovedFiles,
  getAllBranches,
  getAllBins,
  getProductsByBin,
  getReportTypes,
}) => {
  const [, contextHolder] = notification.useNotification();
  const [spinning, setSpinning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<WinnerRow[]>([]);
  const [dynamicFields, setDynamicFields] = useState<FieldDataType[]>([]);
  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  const [savedValues, setSavedValues] = useState<Record<string, string>>({});
  const [dropdownSelection, setDropdownSelection] = useState<Record<string, string>>({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tags, setTags] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [applicableOperations, setApplicableOperations] = useState<any[]>([]);
  const [getFields, setGetFields] = useState(createCampaign_Red.getField || []);
  const [unapprovedFiles, setUnapprovedFiles] = useState<{ label: string; value: string }[]>([]);
  const [reportTypeOptions, setReportTypeOptions] = useState<{ label: string; value: string }[]>([]);
  const pendingFileId = React.useRef<string | null>(null);

  // Dropdown field options state
  const [branchOptions, setBranchOptions] = useState<{ label: string; value: string }[]>([]);
  const [binOptions, setBinOptions] = useState<{ label: string; value: string }[]>([]);
  const [productOptions, setProductOptions] = useState<{ label: string; value: string }[]>([]);
  const [dropdownFieldLoading, setDropdownFieldLoading] = useState(false);
  const [selectedBinIds, setSelectedBinIds] = useState<number[]>([]);
  const binIdMapRef = React.useRef<Record<string, number>>({});
  const [selectedProductValues, setSelectedProductValues] = useState<string[]>([]);
  const [productFieldId, setProductFieldId] = useState<number | null>(null);
  const [activeDropdownField, setActiveDropdownField] = useState<'branch' | 'bin' | 'product' | null>(null);
  const [fieldData, setFieldData] = useState<FieldDataType>({
    fieldId: 0,
    fieldName: '',
    fieldType: '',
    fieldOperation: '',
    fieldOperationLabel: '',
    fieldValue: '',
    fieldValuePlaceholder: '',
    fieldRangeValue: { value1: '', value2: '' },
    fieldArrValue: [],
  });

  useEffect(() => {
    if (createCampaign_Red.getField) {
      setGetFields(createCampaign_Red.getField);
    }
  }, [createCampaign_Red.getField]);

  useEffect(() => {
    const fetchFields = async () => {
      setLoading(true);
      try {
        await getField();
        const res = await getUnapprovedFiles();
        if (res?.success && Array.isArray(res.data)) {
          setUnapprovedFiles(
            res.data
              .filter((f: any) => f.isApproved === true)
              .map((f: any) => ({ label: f.fileName ?? f.name ?? f.fileId, value: String(f.fileId) }))
          );
          if (pendingFileId.current) {
            setValue('fileId', pendingFileId.current);
            pendingFileId.current = null;
          }
        }

        const rtRes = await getReportTypes();
        if (rtRes?.success && Array.isArray(rtRes.data)) {
          setReportTypeOptions(rtRes.data.map((rt: string) => ({
            label: rt.replace(/_/g, ' '), 
            value: rt,
          })));
        }
      } catch (error) {
        console.error('Error fetching fields:', error);
        notification.error({
          message: 'Error',
          description: 'Failed to fetch fields. Please try again.',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchFields();
  }, [getField, getUnapprovedFiles]);

  const fieldOptions = (getFields || []).map((field: any) => ({
    label: field.filterFieldsName,
    value: field.filterFieldsId,
    type: field.fieldType,
    valuePlaceholder: field.valuePlaceholder || ''
  }));

  const toInputType = (fieldType: string): string => {
    if (fieldType === 'string') return 'text';
    if (fieldType === 'number') return 'number';
    if (fieldType === 'date') return 'date';
    return 'text';
  };

  const SPECIAL_FIELD_KEYWORDS = {
    branch: ['branch', 'branch_identifier', 'branchidentifier'],
    bin: ['bin', 'bin_identifier', 'binidentifier'],
    product: ['product', 'product_code', 'productcode'],
  };

  const detectSpecialField = (fieldName: string): 'branch' | 'bin' | 'product' | null => {
    const lower = fieldName.toLowerCase().replace(/\s/g, '');
    if (SPECIAL_FIELD_KEYWORDS.branch.some(k => lower.includes(k))) return 'branch';
    if (SPECIAL_FIELD_KEYWORDS.bin.some(k => lower.includes(k))) return 'bin';
    if (SPECIAL_FIELD_KEYWORDS.product.some(k => lower.includes(k))) return 'product';
    return null;
  };

  // Load branches when branch field is selected
  const loadBranches = async () => {
    setDropdownFieldLoading(true);
    try {
      const decoded = getDecodedToken();
      const institutionId = decoded?.institutionId ?? 0;
      const res = await getAllBranches(institutionId);
      if (res?.success && Array.isArray(res.data)) {
        setBranchOptions(res.data.map((b: any) => ({
          label: b.branchIdentifier || b.branch_identifier,
          value: b.branchIdentifier || b.branch_identifier,
        })));
      } else {
        console.error('Failed to load branches:', res?.message);
      }
    } catch (e) {
      console.error('Failed to load branches', e);
    } finally {
      setDropdownFieldLoading(false);
    }
  };

  // Load BINs when bin field is selected
  const loadBins = async () => {
    setDropdownFieldLoading(true);
    try {
      const res = await getAllBins();
      if (res?.success && Array.isArray(res.data)) {
        const idMap: Record<string, number> = {};
        res.data.forEach((b: any) => {
          if (b.bin) idMap[b.bin] = b.id;
        });
        binIdMapRef.current = idMap;

        setBinOptions(
          res.data
            .filter((b: any) => b.bin)
            .map((b: any) => ({ label: b.bin, value: b.bin }))
        );
        setProductOptions([]);
      }
    } catch (e) {
      console.error('Failed to load BINs', e);
    } finally {
      setDropdownFieldLoading(false);
    }
  };

  const loadProductsForBin = async (binId: number) => {
    setDropdownFieldLoading(true);
    try {
      const res = await getProductsByBin(binId);
      if (res?.success && Array.isArray(res.data)) {
        setProductOptions(res.data.map((p: any) => ({
          label: `${p.productName} (${p.productCode})`,
          value: p.productCode,
        })));
      }
    } catch (e) {
      console.error('Failed to load products', e);
    } finally {
      setDropdownFieldLoading(false);
    }
  };

  // Load all products independently (no BIN required)
  const loadAllProducts = async () => {
    setDropdownFieldLoading(true);
    try {
      const res = await getProductsByBin(0);
      if (res?.success && Array.isArray(res.data)) {
        setProductOptions(res.data.map((p: any) => ({
          label: `${p.productName} (${p.productCode})`,
          value: p.productCode,
        })));
      }
    } catch (e) {
      console.error('Failed to load all products', e);
    } finally {
      setDropdownFieldLoading(false);
    }
  };



  const {
    control,
    watch,
    formState: { errors },
    handleSubmit,
    getValues,
    setValue
  } = useForm<CampaignForm>({
    defaultValues: {
      Campaign: '',
      StartDate: '',
      EndDate: '',
      WinnerAnnouncement: '',
      WinnerBranchExcluded: '',
      NoOfWinners: 0,
      WinnerAnnouncementSelection: '',
      CustomerExcluded: '',
      BlackListedCustomerExcluded: '',
      Prize: '',
      Amount: '',
      Asset: '',
      Description: '',
      fileId: '',
      reportType: '',
      isMockCampaign: 'No',
    },
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
  });

  const {
    control: modalControl,
    formState: { errors: modalErrors },
    handleSubmit: handleModalSubmit,
    reset: resetModal,
    getValues: getModalValues,
  } = useForm<FieldDataType>({
    defaultValues: {
      fieldId: 0,
      fieldName: '',
      fieldType: '',
      fieldOperation: '',
      fieldValue: '',
      fieldOperationLabel: '',
      fieldValuePlaceholder: '',
      fieldRangeValue: { value1: '', value2: '' },
      fieldArrValue: [],
    },
    mode: 'onChange',
  });

  const winnerAnnouncement = watch('WinnerAnnouncement');
  const prizeType = watch('Prize');
  const amount = watch('Amount');
  const asset = watch('Asset');

  useEffect(() => {
    if (rows.length > 0) {
      const currentPrizeValue = prizeType === 'Asset' ? asset : amount;
      
      setDropdownSelection(prev => {
        const updated = { ...prev };
        rows.forEach(row => { updated[row.key] = prizeType || ''; });
        return updated;
      });
      
      setInputValues(prev => {
        const updated = { ...prev };
        rows.forEach(row => { updated[row.key] = currentPrizeValue || ''; });
        return updated;
      });
      
      setRows(prev => prev.map(row => ({
        ...row,
        prizeType: prizeType || '',
        prizeValue: currentPrizeValue || ''
      })));
    }
  }, [prizeType, amount, asset]);

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    resetModal();
    setTags([]);
    setInputValue('');
    setFieldData({
      fieldId: 0,
      fieldName: '',
      fieldType: '',
      fieldOperation: '',
      fieldOperationLabel: '',
      fieldValue: '',
      fieldValuePlaceholder: '',
      fieldRangeValue: { value1: '', value2: '' },
      fieldArrValue: [],
    });
    setActiveDropdownField(null);
    setProductOptions([]);
    setSelectedBinIds([]);
    setSelectedProductValues([]);
    setProductFieldId(null);
    setIsModalOpen(false);
  };




  const calculateRange = () => {
    setLoading(true);

    const formValues = getValues();
    const { WinnerAnnouncement: winnerType, NoOfWinners: noOfWinners, StartDate: startDateValue, EndDate: endDateValue } = formValues;
    const prizeValue = formValues.Prize === 'Asset' ? formValues.Asset : formValues.Amount;

    if (!noOfWinners || noOfWinners <= 0) {
      notification.error({
        message: 'Invalid Number of Winners',
        description: 'Please enter a valid number of winners.',
      });
      setLoading(false);
      return;
    }

    if (!Number.isInteger(Number(noOfWinners))) {
      notification.error({
        message: 'Invalid Number of Winners',
        description: 'Number of winners must be a whole number.',
      });
      setLoading(false);
      return;
    }

    if (noOfWinners > 100) {
      notification.error({
        message: 'Invalid Number of Winners',
        description: 'Maximum 100 winners allowed.',
      });
      setLoading(false);
      return;
    }

    if (!startDateValue || !endDateValue) {
      notification.error({
        message: 'Invalid Dates',
        description: 'Both Start Date and End Date are required.',
      });
      setLoading(false);
      return;
    }

    const startDate = new Date(startDateValue);
    const endDate = new Date(endDateValue);

    if (startDate >= endDate && winnerType !== 'End Of Campaign') {
      notification.error({
        message: 'Invalid Date Range',
        description: 'Start Date must be before End Date.',
      });
      setLoading(false);
      return;
    }

    const generatedRows: WinnerRow[] = [];
    const inputValuesMap: Record<string, string> = {};
    const dropdownMap: Record<string, string> = {};

    if (winnerType === 'End Of Campaign') {
      for (let i = 0; i < noOfWinners; i++) {
        const key = `end-campaign-${i}`;
        generatedRows.push({
          key,
          period: 'End Of Campaign',
          startDate,
          endDate,
          winnerNumber: `Winner ${i + 1}`,
          customerExcluded: formValues.CustomerExcluded,
          winnerAnnouncementSelection: formValues.WinnerAnnouncementSelection,
          prizeType: formValues.Prize || '',
          prizeValue: prizeValue || ''
        });
        dropdownMap[key] = formValues.Prize || '';
        inputValuesMap[key] = prizeValue || '';
      }
    } else {
      let periodIndex = 0;
      const isWeekly = winnerType === 'Weekly Winner';
      let currentStartDate = new Date(startDate);

      while (currentStartDate <= endDate) {
        const nextEndDate = new Date(currentStartDate);
        
        if (isWeekly) {
          nextEndDate.setDate(nextEndDate.getDate() + 6);
        } else {
          nextEndDate.setMonth(nextEndDate.getMonth() + 1);
          nextEndDate.setDate(nextEndDate.getDate() - 1);
        }

        const periodEndDate = nextEndDate > endDate ? endDate : nextEndDate;

        for (let i = 0; i < noOfWinners; i++) {
          const key = `${periodIndex}-${i}`;
          generatedRows.push({
            key,
            period: `${isWeekly ? 'Week' : 'Month'} ${periodIndex + 1}`,
            startDate: new Date(currentStartDate),
            endDate: periodEndDate,
            winnerNumber: `Winner ${i + 1}`,
            customerExcluded: formValues.CustomerExcluded,
            winnerAnnouncementSelection: formValues.WinnerAnnouncementSelection,
            prizeType: formValues.Prize || '',
            prizeValue: prizeValue || ''
          });
          dropdownMap[key] = formValues.Prize || '';
          inputValuesMap[key] = prizeValue || '';
        }

        currentStartDate = new Date(periodEndDate);
        currentStartDate.setDate(currentStartDate.getDate() + 1);
        periodIndex++;
      }
    }

    setRows(generatedRows);
    setInputValues(inputValuesMap);
    setDropdownSelection(dropdownMap);
    
    const isWeekly = winnerType === 'Weekly Winner';
    notification.success({
      message: 'Range Calculated',
      description: `Successfully generated ${winnerType === 'End Of Campaign'
        ? `${noOfWinners} winners`
        : `${Math.ceil(generatedRows.length / noOfWinners)} ${isWeekly ? 'weeks' : 'months'}`
        }.`,
    });
    
    setLoading(false);
  };

  const winnerColumns = [
    {
      title: 'Period',
      dataIndex: 'period',
      key: 'period',
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (date: Date | string) => date instanceof Date ? date.toLocaleDateString() : date || '-',
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (date: Date | string) => {
        if (date instanceof Date) {
          return date.toLocaleDateString();
        }
        return date || '-';
      },
    },
    {
      title: 'Winner Number',
      dataIndex: 'winnerNumber',
      key: 'winnerNumber',
    },
    {
      title: 'Asset/Prize',
      dataIndex: 'prizeType',
      key: 'prizeType',
      render: (_: any, record: WinnerRow) => (
        <Dropdown
          menu={{
            items: PRIZE_TYPE_OPTIONS.map(option => ({
              label: option.label,
              key: option.value,
              onClick: () => {
                setDropdownSelection(prev => ({
                  ...prev,
                  [record.key]: option.value,
                }));
                setInputValues(prev => ({
                  ...prev,
                  [record.key]: record.prizeValue || '',
                }));
              },
            }))
          }}
          trigger={['click']}
        >
          <Space>
            {dropdownSelection[record.key] || record.prizeType}
            <DownOutlined />
          </Space>
        </Dropdown>
      ),
    },
    {
      title: 'Values',
      dataIndex: 'prizeValue',
      key: 'prizeValue',
      render: (_: any, record: WinnerRow) => {
        const selection = dropdownSelection[record.key] || record.prizeType;
        const values = inputValues[record.key] || '';
        const savedValue = savedValues[record.key];

        return savedValue && !selection ? (
          <span>{savedValue}</span>
        ) : (
          <Input
            placeholder={`Enter ${selection}`}
            value={values}
            type={selection === 'Prize' ? 'number' : 'text'}
            onChange={(e) => {
              setInputValues(prev => ({
                ...prev,
                [record.key]: e.target.value,
              }));
            }}
            style={{ width: 200 }}
          />
        );
      },
    }
  ];

  const fieldColumns = [
    {
      title: 'Field Name',
      dataIndex: 'fieldName',
      key: 'fieldName',
    },
    {
      title: 'Field Type',
      dataIndex: 'fieldType',
      key: 'fieldType',
    },
    {
      title: 'Filter Type',
      dataIndex: 'fieldOperationLabel',
      key: 'fieldOperationLabel',
      render: (text: string) => text || '-',
    },
    {
      title: 'Values',
      dataIndex: 'fieldValue',
      key: 'fieldValue',
      render: (text: string) => {
        if (!text || text === 'N/A') return '-';
        // Try parse JSON array: ["val1","val2"]
        try {
          const parsed = JSON.parse(text);
          if (Array.isArray(parsed)) {
            return (
              <span>
                {parsed.map((v: string, i: number) => (
                  <Tag key={i} style={{ marginBottom: 2 }}>{v}</Tag>
                ))}
              </span>
            );
          }
        } catch (_) {}
        // comma-separated fallback
        if (text.includes(',')) {
          return (
            <span>
              {text.split(',').map((v, i) => (
                <Tag key={i} style={{ marginBottom: 2 }}>{v.trim()}</Tag>
              ))}
            </span>
          );
        }
        return text;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any, index: number) => (
        <FaTrash
          className={style.deleteIcon}
          onClick={() => handleDeleteField(index)}
          title="Delete Field"
          style={{ cursor: 'pointer', color: '#ff4d4f' }}
        />
      ),
    },
  ];




  const onModalSubmit: SubmitHandler<FieldDataType> = (data) => {
    const startValue = getModalValues('fieldRangeValue.value1') || '';
    const endValue = getModalValues('fieldRangeValue.value2') || '';

    let fieldValue = '';
    if (fieldData.fieldOperation === 'between') {
      fieldValue = `${startValue},${endValue}`;
    } else if (['in', 'not in'].includes(fieldData.fieldOperation)) {
      // Store comma-separated — createCampaignData converts to array
      const arrValues = activeDropdownField ? fieldData.fieldArrValue : tags;
      fieldValue = arrValues.join(',');
    } else if (fieldData.fieldOperation === '=' && activeDropdownField) {
      // Store plain value — createCampaignData wraps in array
      fieldValue = fieldData.fieldValue || '';
    } else if (fieldData.fieldOperation === 'like') {
      // Format value as %value% (contains)
      const rawValue = data.fieldValue || '';
      fieldValue = `%${rawValue}%`;
    } else if (['=', '!=', '<', '<=', '>', '>='].includes(fieldData.fieldOperation)) {
      fieldValue = data.fieldValue || '';
    }

    const newField: FieldDataType = {
      fieldId: fieldData.fieldId,
      fieldName: fieldData.fieldName || 'Unknown Field',
      fieldType: fieldData.fieldType || data.fieldType,
      fieldOperation: fieldData.fieldOperation,
      fieldOperationLabel: fieldData.fieldOperationLabel,
      fieldValue: fieldValue || 'N/A',
      fieldValuePlaceholder: fieldData.fieldValuePlaceholder,
      fieldRangeValue: {
        value1: startValue,
        value2: endValue,
      },
      fieldArrValue: activeDropdownField ? fieldData.fieldArrValue : tags,
    };

    const newFields: FieldDataType[] = [newField];

    // If BIN field with products selected → add a separate product filter entry
    if (activeDropdownField === 'bin' && selectedProductValues.length > 0 && productFieldId) {
      const productField = fieldOptions.find((f: any) => f.value === productFieldId);
      const productEntry: FieldDataType = {
        fieldId: productFieldId,
        fieldName: productField?.label || 'Product',
        fieldType: productField?.type || 'string',
        fieldOperation: fieldData.fieldOperation,
        fieldOperationLabel: fieldData.fieldOperationLabel,
        // Store comma-separated — createCampaignData converts to array
        fieldValue: selectedProductValues.join(','),
        fieldValuePlaceholder: '',
        fieldRangeValue: { value1: '', value2: '' },
        fieldArrValue: selectedProductValues,
      };
      newFields.push(productEntry);
    }

    const isValueEmpty = (val: string) => !val || val.trim() === '' || val === 'N/A' || val === '%' || val === '%%' || val === ',';

    if (isValueEmpty(newField.fieldValue)) {
      notification.warning({
        message: 'Value Required',
        description: 'Please enter a filter value before saving.',
      });
      return;
    }

    if (fieldData.fieldOperation === 'between') {
      if (isValueEmpty(startValue) || isValueEmpty(endValue)) {
        notification.warning({
          message: 'Both Values Required',
          description: 'Please enter both start and end values for the range filter.',
        });
        return;
      }
    }

    if (['in', 'not in'].includes(fieldData.fieldOperation)) {
      const arrValues = activeDropdownField ? fieldData.fieldArrValue : tags;
      if (!arrValues || arrValues.length === 0) {
        notification.warning({
          message: 'Value Required',
          description: 'Please add at least one value for the list filter.',
        });
        return;
      }
    }

    setDynamicFields(prevFields => [...prevFields, ...newFields]);
    handleCancel();
  };






  const formatTime = (date: Date): string => {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  };

  const formatDate = (date: string | Date): string => {
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return '';
    const year = d.getFullYear();
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const day = d.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const createCampaignData = (data: CampaignForm, isUpdate = false) => {
    const currentTime = new Date();
    const institutionId = getDecodedToken()?.institutionId;

    const baseData = {
      campaignName: data.Campaign,
      startDate: data.StartDate ? formatDate(data.StartDate) : null,
      startTime: formatTime(currentTime),
      endDate: data.EndDate ? formatDate(data.EndDate) : null,
      endTime: formatTime(currentTime),
      winnerFrequency: data.WinnerAnnouncement,
      noOfWinners: data.NoOfWinners,
      winningCustomerExcluded: data.CustomerExcluded === 'Yes',
      blackListedCustomerExcluded: data.BlackListedCustomerExcluded === 'Yes',
      winnerAnnouncementSelection: data.WinnerAnnouncementSelection,
      isWinnerBranchExcluded: data.WinnerBranchExcluded === 'Yes',
      campaignStatus: 'Pending',
      isApproved: true,
      approvedBy: 0,
      description: data.Description,
      fileId: data.fileId ? Number(data.fileId) : undefined,
      reportType: data.reportType || undefined,
      isMockCampaign: data.isMockCampaign === 'Yes',
      ...(institutionId !== undefined && { institutionId }),
      campaignFilters: [
        ...dynamicFields.map(field => {
          let filterValue: any = field.fieldValue || '';

          if (field.fieldOperation === 'between') {
            // between → string containing JSON array: "[\"2024-01-01\",\"2024-12-31\"]"
            try {
              const parsed = JSON.parse(filterValue);
              if (Array.isArray(parsed)) {
                filterValue = JSON.stringify(parsed);
              } else {
                const parts = filterValue.split(',').map((s: string) => s.trim());
                filterValue = JSON.stringify([parts[0] || '', parts[1] || '']);
              }
            } catch (_) {
              const parts = filterValue.split(',').map((s: string) => s.trim());
              filterValue = JSON.stringify([parts[0] || '', parts[1] || '']);
            }
          } else if (['in', 'not in'].includes(field.fieldOperation)) {
            // in/not in → string containing JSON array: "[\"001\",\"002\"]"
            try {
              const parsed = JSON.parse(filterValue);
              if (Array.isArray(parsed)) {
                filterValue = JSON.stringify(parsed);
              } else {
                const parts = filterValue.split(',').map((s: string) => s.trim()).filter(Boolean);
                filterValue = JSON.stringify(parts);
              }
            } catch (_) {
              const parts = filterValue.split(',').map((s: string) => s.trim()).filter(Boolean);
              filterValue = JSON.stringify(parts);
            }
          } else {
            // All other ops (=, >, <, etc.) → plain string
            try {
              const parsed = JSON.parse(filterValue);
              if (Array.isArray(parsed)) filterValue = parsed.join(',');
            } catch (_) {}
            filterValue = String(filterValue).trim();
          }

          return {
            filterOperation: field.fieldOperation,
            filterValue,
            filterFieldId: field.fieldId,
          };
        }),
      ],
      campaignWinners: rows.reduce((acc: any[], row) => {
        const { startDate, endDate } = row;
        const period = row.period;
        const numberOfWinners = 1;

        for (let i = 0; i < numberOfWinners; i++) {
          acc.push({
            period,
            winningStartRange: formatDate(startDate),
            winningEndRange: formatDate(endDate),
            winningPosition: acc.filter(w => w.period === period).length + 1,
            prizeType: dropdownSelection[row.key] || 'string',
            prizeAmount: dropdownSelection[row.key] === 'Prize' ? inputValues[row.key] : null,
            prizeGift: dropdownSelection[row.key] === 'Asset' ? inputValues[row.key] : null,
            bankTransactionId: null,
            customerIdentifier: null,
            branchIdentifier: null,
            accountShortName: null,
          });
        }
        return acc;
      }, []),
    };

    return isUpdate ? { ...baseData, campaignId: currentCampaignId } : baseData;
  };

  const onMainSubmit: SubmitHandler<CampaignForm> = async (data) => {
    if (rows.length === 0) {
      notification.error({
        message: 'Winners Not Calculated',
        description: 'Please click Calculate to generate winners before creating the campaign.',
      });
      return;
    }

    setSpinning(true);
    
    try {
      const campaignData = createCampaignData(data);
      const response = await createCampaign(campaignData);
      
      if (response.success) {
        notification.success({
          message: 'Campaign Created',
          description: 'Campaign created successfully.',
        });
        setTimeout(() => { Back(); }, 2000);
      } else {
        notification.error({
          message: 'Campaign Creation Failed',
          description: response.message || 'An error occurred while creating the campaign.',
        });
      }
    } catch (error) {
      console.error('Campaign creation error:', error);
      notification.error({
        message: 'Error',
        description: 'An error occurred while creating the campaign.',
      });
    } finally {
      setSpinning(false);
    }
  };




  const updateSubmit: SubmitHandler<CampaignForm> = async (data) => {
    setSpinning(true);
    
    try {
      const campaignData = createCampaignData(data, true);
      const response = await updateCampaign(campaignData);
      
      if (response.status === '00') {
        notification.success({
          message: 'Updated Successfully',
          description: 'Campaign updated successfully.',
        });
        Back();
      } else {
        notification.error({
          message: 'Campaign Update Failed',
          description: response.message || 'An error occurred while updating the campaign.',
        });
      }
    } catch (error) {
      console.error('Campaign update error:', error);
      notification.error({
        message: 'Error',
        description: 'An error occurred while updating the campaign.',
      });
    } finally {
      setSpinning(false);
    }
  };


  const handleDeleteField = (index: number) => {
    setDynamicFields(prevFields => prevFields.filter((_, i) => i !== index));
  };

  const handleInputConfirm = () => {
    if (inputValue && !tags.includes(inputValue)) {
      const newTags = [...tags, inputValue];
      setTags(newTags);
      setFieldData(prev => ({ ...prev, fieldArrValue: newTags }));
    }
    setInputValue('');
  };

  const handleTagClose = (removedTag: string) => {
    const newTags = tags.filter(tag => tag !== removedTag);
    setTags(newTags);
    setFieldData(prev => ({ ...prev, fieldArrValue: newTags }));
  };

  const handleBack = () => {
    setSpinning(true);
    setTimeout(() => {
      setSpinning(false);
      Back();
    }, 1000);
  };





  useEffect(() => {
    if (!currentCampaignId) return;

    const loadCampaignData = async () => {
      setSpinning(true);
      try {
        const campaignData = await getByID(currentCampaignId);
        
        if (campaignData) {
          // Set form values
          setValue('Campaign', campaignData.campaignName || '');
          setValue('StartDate', campaignData.startDate || null);
          setValue('EndDate', campaignData.endDate || null);
          setValue('WinnerAnnouncement', campaignData.winnerFrequency || '');
          setValue('WinnerBranchExcluded', campaignData.isWinnerBranchExcluded ? 'Yes' : 'No');
          setValue('CustomerExcluded', campaignData.winningCustomerExcluded ? 'Yes' : 'No');
          setValue('BlackListedCustomerExcluded', campaignData.blackListedCustomerExcluded ? 'Yes' : 'No');
          setValue('isMockCampaign', campaignData.isMockCampaign ? 'Yes' : 'No');
          setValue('WinnerAnnouncementSelection', campaignData.winnerAnnouncementSelection || '');
          setValue('NoOfWinners', campaignData.noOfWinners || 0);
          setValue('Description', campaignData.description || '');
          setValue('fileId', campaignData.fileId ? String(campaignData.fileId) : '');
          setValue('reportType', campaignData.reportType || '');
          pendingFileId.current = campaignData.fileId ? String(campaignData.fileId) : null;

          if (campaignData.campaignWinners && campaignData.campaignWinners.length > 0) {
            const firstWinner = campaignData.campaignWinners[0];
            setValue('Prize', firstWinner.prizeType || '');
            if (firstWinner.prizeType === 'Prize') {
              setValue('Amount', firstWinner.prizeAmount || '');
            } else if (firstWinner.prizeType === 'Asset') {
              setValue('Asset', firstWinner.prizeGift || '');
            }
          }

          const mappedFields = campaignData.campaignFilters?.map((filter: any) => {
            const field = getFields.find((f: any) => f.filterFieldsId === filter.filterFieldId);
            const filterValue: string = filter.filterValue || '';

            // Safely parse filterValue - supports both JSON array and comma-separated string
            const parseArrValue = (val: string): string[] => {
              if (!val) return [];
              // Try JSON parse first (old format: ["a","b"])
              try {
                const parsed = JSON.parse(val);
                if (Array.isArray(parsed)) return parsed.map(String);
              } catch (_) {}
              // Fallback: comma-separated string (new format: "a,b,c")
              return val.split(',').map(s => s.trim()).filter(Boolean);
            };

            // For between: ["1987-01-01", "2001-12-31"] or "val1,val2"
            const parseBetweenValue = (val: string): [string, string] => {
              if (!val) return ['', ''];
              // JSON array format
              try {
                const parsed = JSON.parse(val);
                if (Array.isArray(parsed)) return [String(parsed[0] || ''), String(parsed[1] || '')];
              } catch (_) {}
              // Plain comma fallback
              const parts = val.split(',').map(s => s.trim().replace(/^'|'$/g, ''));
              return [parts[0] || '', parts[1] || ''];
            };

            const [betweenVal1, betweenVal2] = filter.filterOperation === 'between'
              ? parseBetweenValue(filterValue)
              : ['', ''];

            return {
              fieldId: filter.filterFieldId || 0,
              fieldName: field?.filterFieldsName || 'Unknown Field',
              fieldType: field?.fieldType || 'Unknown Field',
              fieldOperation: filter.filterOperation || '',
              fieldOperationLabel: OPERATIONS.find((op: any) => op.operator === filter.filterOperation)?.label || '',
              fieldValue: filterValue,
              fieldValuePlaceholder: field?.valuePlaceholder || '',
              fieldRangeValue: filter.filterOperation === 'between'
                ? { value1: betweenVal1, value2: betweenVal2 }
                : { value1: '', value2: '' },
              fieldArrValue: ['in', 'not in'].includes(filter.filterOperation)
                ? parseArrValue(filterValue)
                : [],
            };
          }) || [];
          
          setDynamicFields(mappedFields);

          const rowsData = campaignData.campaignWinners?.map((winner: any, index: number) => {
            const startDate = new Date(winner.winningStartRange);
            const endDate = new Date(winner.winningEndRange);
            const key = `winner-${index}`;
            const prizeType = winner.prizeType || '';
            const prizeValue = prizeType === 'Prize' ? winner.prizeAmount : winner.prizeGift;

            setDropdownSelection(prev => ({ ...prev, [key]: prizeType }));
            setInputValues(prev => ({ ...prev, [key]: prizeValue }));

            return {
              key,
              period: `Month ${Math.ceil((startDate.getMonth() + 1))}`,
              startDate,
              endDate,
              winnerNumber: `Winner ${winner.winningPosition}`,
              customerExcluded: campaignData.winningCustomerExcluded ? 'Yes' : 'No',
              winnerAnnouncementSelection: campaignData.winnerAnnouncementSelection || '',
              prizeType,
              prizeValue: prizeValue || '',
            };
          }) || [];

          setRows(rowsData);
        }
      } catch (error) {
        console.error('Error fetching campaign data:', error);
        notification.error({
          message: 'Error',
          description: 'Failed to load campaign data.',
        });
      } finally {
        setSpinning(false);
      }
    };

    loadCampaignData();
  }, [currentCampaignId, getFields, setValue, getByID]);

  return (
    <>
      {contextHolder}
      <Spin spinning={spinning} fullscreen />
      
      <div className={`container-fluid ${style.Container}`}>
        <div className={style.addbtn}>
          <FaArrowLeft 
            size={24} 
            onClick={handleBack} 
            className={style.backArrow} 
            style={{ cursor: 'pointer' }}
          />
          <Button 
            Text='Add Field' 
            buttonClass={style.buttonSignIn} 
            Disable={spinning} 
            onClick={showModal} 
          />
        </div>
        
        <div className={`row ${style.row}`}>
          <div className={`col-lg-12 col-md-12 col-sm-12 ${style.SignInContainer}`}>
            <form onSubmit={handleSubmit(currentCampaignId ? updateSubmit : onMainSubmit)}>
              {/* Campaign Basic Information */}
              <div className={style.formSetting}>
                <div className={style.field}>
                  <FormInput
                    Label='Campaign Name'
                    placeholder='Campaign Name'
                    classInput={style.Input}
                    className={style.inputContainer}
                    labelClass={style.Label}
                    classError={style.Error}
                    name='Campaign'
                    type='text'
                    errors={errors}
                    control={control}
                  />
                </div>
                <div className={style.field}>
                  <FormInput
                    Label='Start Date'
                    placeholder='Start Date'
                    classInput={style.Input}
                    className={style.inputContainer}
                    labelClass={style.Label}
                    classError={style.Error}
                    name='StartDate'
                    type='date'
                    errors={errors}
                    control={control}
                  />
                </div>
                <div className={style.field}>
                  <FormInput
                    Label='End Date'
                    placeholder='End Date'
                    classInput={style.Input}
                    className={style.inputContainer}
                    labelClass={style.Label}
                    classError={style.Error}
                    name='EndDate'
                    type='date'
                    errors={errors}
                    control={control}
                    min={watch('StartDate') || undefined}
                    disabled={!watch('StartDate')}
                  />
                </div>
              </div>

              {/* Winner Configuration */}
              <div className={style.formSetting}>
                <div className={style.field}>
                  <FormSelect
                    control={control}
                    options={WINNER_ANNOUNCEMENT_OPTIONS}
                    placeholder='Select Winner Announcement Selection'
                    errors={errors}
                    className={style.inputContainer}
                    classError={style.Error}
                    showLabel={true}
                    Label='Winner Announcement Selection'
                    name='WinnerAnnouncementSelection'
                    labelClass={style.Label}
                    type='WinnerAnnouncementSelection'
                  />
                </div>
                <div className={style.field}>
                  <FormSelect
                    control={control}
                    options={WINNER_TYPE_OPTIONS}
                    placeholder='Select Winner Announcement Type'
                    errors={errors}
                    className={style.inputContainer}
                    classError={style.Error}
                    showLabel={true}
                    Label='Winner Announcement'
                    name='WinnerAnnouncement'
                    labelClass={style.Label}
                    type='WinnerAnnouncement'
                  />
                </div>
                <div className={style.field}>
                  <FormInput
                    Label='No Of Winners'
                    placeholder='No Of Winners'
                    classInput={style.Input}
                    className={style.inputContainer}
                    labelClass={style.Label}
                    classError={style.Error}
                    name='NoOfWinners'
                    type='number'
                    errors={errors}
                    control={control}
                  />
                </div>
              </div>

              {/* Exclusion Settings */}
              <div className={style.formSetting}>
                <div className={style.field}>
                  <FormSelect
                    control={control}
                    name='WinnerBranchExcluded'
                    options={YES_NO_OPTIONS}
                    placeholder='Select Option'
                    errors={errors}
                    className={style.inputContainer}
                    classError={style.Error}
                    showLabel={true}
                    Label='Winner Branch Excluded'
                    labelClass={style.Label}
                    type='WinnerBranchExcluded'
                  />
                </div>
                <div className={style.field}>
                  <FormSelect
                    control={control}
                    name='CustomerExcluded'
                    options={YES_NO_OPTIONS}
                    placeholder='Select Option'
                    errors={errors}
                    className={style.inputContainer}
                    classError={style.Error}
                    showLabel={true}
                    Label='Customer Excluded'
                    labelClass={style.Label}
                    type='CustomerExcluded'
                  />
                </div>
                <div className={style.field}>
                  <FormSelect
                    control={control}
                    name='BlackListedCustomerExcluded'
                    options={YES_NO_OPTIONS}
                    placeholder='Select Option'
                    errors={errors}
                    className={style.inputContainer}
                    classError={style.Error}
                    showLabel={true}
                    Label='Black Listed Customer Excluded'
                    labelClass={style.Label}
                    type='BlackListedCustomerExcluded'
                  />
                </div>
              </div>

              {/* Mock Campaign + Prize Settings */}
              <div className={style.formSetting}>
                <div className={style.field}>
                  <FormSelect
                    control={control}
                    name='isMockCampaign'
                    options={YES_NO_OPTIONS}
                    placeholder='Select Option'
                    errors={errors}
                    className={style.inputContainer}
                    classError={style.Error}
                    showLabel={true}
                    Label='Mock Campaign'
                    labelClass={style.Label}
                    type='isMockCampaign'
                  />
                </div>
                <div className={style.field}>
                  <FormSelect
                    control={control}
                    name='Prize'
                    options={PRIZE_TYPE_OPTIONS}
                    placeholder='Select Prize Type'
                    errors={errors}
                    className={style.inputContainer}
                    classError={style.Error}
                    showLabel={true}
                    Label='Prize Type'
                    labelClass={style.Label}
                    type='Prize'
                  />
                </div>
                    <div className={style.field}>
                  {prizeType === 'Asset' ? (
                    <FormInput
                      Label='Asset'
                      placeholder='Enter Asset'
                      classInput={style.Input}
                      className={style.inputContainer}
                      labelClass={style.Label}
                      classError={style.Error}
                      name='Asset'
                      type='text'
                      errors={errors}
                      control={control}
                    />
                  ) : prizeType === 'Prize' ? (
                    <FormInput
                      Label='Amount'
                      placeholder='Enter Amount'
                      classInput={style.Input}
                      className={style.inputContainer}
                      labelClass={style.Label}
                      classError={style.Error}
                      name='Amount'
                      type='number'
                      errors={errors}
                      control={control}
                    />
                  ) : null}
                </div>
                <div className={style.field}>
                  <FormSelect
                    control={control}
                    name='fileId'
                    options={unapprovedFiles}
                    placeholder='Select File'
                    errors={errors}
                    className={style.inputContainer}
                    classError={style.Error}
                    showLabel={true}
                    Label='File'
                    labelClass={style.Label}
                    loading={loading}
                  />
                </div>
                <div className={style.field}>
                  <FormSelect
                    control={control}
                    name='reportType'
                    options={reportTypeOptions}
                    placeholder='Select Campaign Type'
                    errors={errors}
                    className={style.inputContainer}
                    classError={style.Error}
                    showLabel={true}
                    Label='Campaign Type'
                    labelClass={style.Label}
                    loading={loading}
                  />
                </div>
                
              </div>

              {/* File Select
              <div className={style.formSetting}>
                
              </div> */}

              {/* Prize Value */}
              {/* <div className={style.formSetting}>
                <div className={style.field}>
                  {prizeType === 'Asset' ? (
                    <FormInput
                      Label='Asset'
                      placeholder='Enter Asset'
                      classInput={style.Input}
                      className={style.inputContainer}
                      labelClass={style.Label}
                      classError={style.Error}
                      name='Asset'
                      type='text'
                      errors={errors}
                      control={control}
                    />
                  ) : (
                    <FormInput
                      Label='Amount'
                      placeholder='Enter Amount'
                      classInput={style.Input}
                      className={style.inputContainer}
                      labelClass={style.Label}
                      classError={style.Error}
                      name='Amount'
                      type='number'
                      errors={errors}
                      control={control}
                    />
                  )}
                </div>
                <div className={style.field}>
                  <FormSelect
                    control={control}
                    name='fileId'
                    options={unapprovedFiles}
                    placeholder='Select File'
                    errors={errors}
                    className={style.inputContainer}
                    classError={style.Error}
                    showLabel={true}
                    Label='File'
                    labelClass={style.Label}
                    loading={loading}
                  />
                </div>
              </div> */}

              {/* Winner Table */}
              <div className={style.tableContainer}>
                <div className={style.tableActions}>
                  <AntButton 
                    onClick={calculateRange} 
                    className={style.andtbtn}
                    loading={loading}
                  >
                    Calculate
                  </AntButton>
                </div>
                {rows.length > 0 && (
                  <>
                    <h6>Winners Configuration</h6>
                    <Table
                      columns={winnerColumns}
                      loading={loading}
                      dataSource={rows}
                      pagination={{ pageSize: 10 }}
                      rowKey="key"
                      size="small"
                      scroll={{ x: 'max-content' }}
                    />
                  </>
                )}
              </div>

              {/* Dynamic Fields Table */}
              <div className={style.tableContainer}>
                {dynamicFields.length > 0 && (
                  <>
                    <h6>Campaign Parameters Configuration</h6>
                    <Table
                      columns={fieldColumns}
                      dataSource={dynamicFields.map((field, index) => ({ ...field, key: index }))}
                      pagination={{ pageSize: 10 }}
                      rowKey="key"
                      size="small"
                      scroll={{ x: 'max-content' }}
                    />
                  </>
                )}
              </div>

              {/* Description */}
              <div className={style.formSettingFull}>
                <div className={style.field}>
                  <FormTextArea
                    Label='Description'
                    placeholder='Enter Description'
                    classInput={style.Input}
                    classTextArea={style.classTextArea}
                    labelClass={style.Label}
                    classError={style.Error}
                    name='Description'
                    type='text'
                    errors={errors}
                    control={control}
                  />
                </div>
              </div>

              {/* Submit Button */}
              <Button 
                Text={currentCampaignId ? 'Update Campaign' : 'Create Campaign'} 
                buttonClass={style.buttonSignIn} 
                Disable={spinning} 
              />
            </form>

            {/* Modal for Adding Fields */}
            <Modal 
              title='Add New Field' 
              open={isModalOpen} 
              onCancel={handleCancel} 
              footer={null} 
              className={style.modal}
            >
              <form 
                onSubmit={handleModalSubmit(onModalSubmit)} 
                className={style.selectField}
              >
                <div className={style.field}>
                  <FormSelect
                    loading={loading}
                    control={modalControl}
                    name='FieldType'
                    options={fieldOptions}
                    placeholder='Select Field Type'
                    errors={modalErrors}
                    className={style.inputContainer}
                    value={fieldData.fieldName}
                    classError={style.Error}
                    showLabel={true}
                    Label='Add Parameter'
                    labelClass={style.Label}
                    showSearch={true}
                    filterOption={(input: string, option: any) =>
                      String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    onChange={(value: any) => {
                      const field = fieldOptions.find((x: any) => x.value === value);
                      if (field) {
                        setFieldData({
                          ...fieldData,
                          fieldId: field.value,
                          fieldName: field.label,
                          fieldType: field.type,
                          fieldValuePlaceholder: field.valuePlaceholder,
                          fieldOperation: '',
                          fieldOperationLabel: '',
                          fieldValue: '',
                          fieldRangeValue: { value1: '', value2: '' },
                          fieldArrValue: []
                        });

                        const filteredOperations = OPERATIONS.filter(op => 
                          op.applicableType.includes(field.type.trim())
                        );
                        setApplicableOperations(filteredOperations);

                        // Detect special dropdown field and pre-load options
                        const specialType = detectSpecialField(field.label);
                        setActiveDropdownField(specialType);
                        // Clear both lists before loading fresh
                        setBinOptions([]);
                        setProductOptions([]);
                        setSelectedBinIds([]);
                        setSelectedProductValues([]);
                        setProductFieldId(null);

                        if (specialType === 'branch') loadBranches();
                        if (specialType === 'bin') {
                          loadBins();
                          // Find product field ID from fieldOptions for separate filter entry
                          const productField = fieldOptions.find((f: any) =>
                            detectSpecialField(f.label) === 'product'
                          );
                          if (productField) setProductFieldId(productField.value);
                        }
                        if (specialType === 'product') loadAllProducts();
                      }
                    }}
                  />
                </div>

                <div className={style.field}>
                  <FormSelect
                    control={modalControl}
                    name='FilterType'
                    options={applicableOperations}
                    placeholder='Select Filter'
                    errors={modalErrors}
                    className={style.inputContainer}
                    classError={style.Error}
                    showLabel={true}
                    value={fieldData.fieldOperationLabel}
                    Label='Filter'
                    labelClass={style.Label}
                    onChange={(value: any) => {
                      const operation = applicableOperations.find(x => x.value === value);
                      if (operation) {
                        setFieldData({
                          ...fieldData,
                          fieldOperation: operation.operator,
                          fieldOperationLabel: operation.label,
                          fieldValue: '',
                          fieldRangeValue: { value1: '', value2: '' },
                          fieldArrValue: []
                        });
                        setSelectedProductValues([]);
                        setProductOptions([]);

                        // Re-load options when filter changes for special fields
                        if (activeDropdownField === 'bin') loadBins();
                        if (activeDropdownField === 'branch') loadBranches();
                        if (activeDropdownField === 'product') loadAllProducts();
                      }
                    }}
                  />
                </div>

                {/* Like operator - contains (automatically adds %value%) */}
                {fieldData.fieldOperation === 'like' && (
                  <div className={style.field}>
                    <FormInput
                      Label='Values'
                      placeholder={fieldData.fieldValuePlaceholder || 'Enter value (e.g. Ali → %Ali%)'}
                      classInput={style.Input}
                      className={style.inputContainer}
                      labelClass={style.Label}
                      classError={style.Error}
                      name='fieldValue'
                      type='text'
                      errors={modalErrors}
                      control={modalControl}
                    />
                  </div>
                )}

                {/* Single Value Input - for special fields show dropdown, for regular show text input */}
                {['=', '<', '<=', '>', '>='].includes(fieldData.fieldOperation) && (
                  <div className={style.field}>
                    {/* Branch single-select for Equal To */}
                    {activeDropdownField === 'branch' && fieldData.fieldOperation === '=' && (
                      <>
                        <label className={style.Label}>Select Branch</label>
                        <Select
                          placeholder="Select a branch"
                          loading={dropdownFieldLoading}
                          options={branchOptions}
                          value={fieldData.fieldValue || undefined}
                          onChange={(value: string) => {
                            setFieldData(prev => ({ ...prev, fieldValue: value, fieldArrValue: [value] }));
                          }}
                          style={{ width: '100%' }}
                          allowClear
                        />
                      </>
                    )}

                    {/* BIN single-select for Equal To */}
                    {activeDropdownField === 'bin' && fieldData.fieldOperation === '=' && (
                      <>
                        <label className={style.Label}>Select BIN</label>
                        <Select
                          placeholder="Select a BIN"
                          loading={dropdownFieldLoading}
                          options={binOptions}
                          value={fieldData.fieldValue || undefined}
                          onChange={(value: string) => {
                            // Store BIN value in fieldData — product stored separately
                            setFieldData(prev => ({ ...prev, fieldValue: value, fieldArrValue: [value] }));
                            setSelectedProductValues([]);
                            const binNumericId = binIdMapRef.current[value];
                            if (binNumericId) loadProductsForBin(binNumericId);
                          }}
                          style={{ width: '100%', marginBottom: 12 }}
                          allowClear
                        />
                        {productOptions.length > 0 && (
                          <>
                            <label className={style.Label} style={{ marginTop: 8 }}>Select Product (Optional)</label>
                            <Select
                              placeholder="Select a product"
                              loading={dropdownFieldLoading}
                              options={productOptions}
                              value={selectedProductValues[0] || undefined}
                              onChange={(value: string) => {
                                // Store product SEPARATELY — does NOT overwrite BIN fieldValue
                                setSelectedProductValues(value ? [value] : []);
                              }}
                              style={{ width: '100%' }}
                              allowClear
                            />
                          </>
                        )}
                      </>
                    )}

                    {/* Product single-select for Equal To */}
                    {activeDropdownField === 'product' && fieldData.fieldOperation === '=' && (
                      <>
                        <label className={style.Label}>Select Product</label>
                        <Select
                          placeholder="Select a product"
                          loading={dropdownFieldLoading}
                          options={productOptions}
                          value={fieldData.fieldValue || undefined}
                          onChange={(value: string) => {
                            setFieldData(prev => ({ ...prev, fieldValue: value, fieldArrValue: [value] }));
                          }}
                          style={{ width: '100%' }}
                          allowClear
                        />
                      </>
                    )}

                    {/* Regular text input for non-special fields or non-equal operations */}
                    {(activeDropdownField === null || !['branch', 'bin', 'product'].includes(activeDropdownField!) || fieldData.fieldOperation !== '=') &&
                      activeDropdownField === null && (
                      <FormInput
                        Label='Values'
                        placeholder={fieldData.fieldValuePlaceholder}
                        classInput={style.Input}
                        className={style.inputContainer}
                        labelClass={style.Label}
                        classError={style.Error}
                        name='fieldValue'
                        type={toInputType(fieldData.fieldType)}
                        errors={modalErrors}
                        control={modalControl}
                      />
                    )}
                  </div>
                )}

                {/* List Input - Branch/BIN/Product special dropdown OR regular tag input */}
                {['in', 'not in'].includes(fieldData.fieldOperation) && (
                  <div className={style.field}>
                    {/* Branch multi-select */}
                    {activeDropdownField === 'branch' && (
                      <>
                        <label className={style.Label}>Select Branches</label>
                        <Select
                          mode="multiple"
                          placeholder="Select branches"
                          loading={dropdownFieldLoading}
                          options={branchOptions}
                          value={fieldData.fieldArrValue}
                          onChange={(values: string[]) => {
                            setFieldData(prev => ({ ...prev, fieldArrValue: values, fieldValue: values.join(',') }));
                          }}
                          style={{ width: '100%' }}
                          allowClear
                        />
                      </>
                    )}

                    {/* BIN multi-select + Product multi-select */}
                    {activeDropdownField === 'bin' && (
                      <>
                        <label className={style.Label}>Select BINs</label>
                        <Select
                          mode="multiple"
                          placeholder="Select BINs"
                          loading={dropdownFieldLoading}
                          options={binOptions}
                          value={fieldData.fieldArrValue}
                          onChange={(values: string[]) => {
                            setFieldData(prev => ({ ...prev, fieldArrValue: values, fieldValue: values.join(',') }));
                            // Get numeric IDs from bin→id map for product API call
                            const ids = values.map(bin => binIdMapRef.current[bin]).filter(Boolean);
                            setSelectedBinIds(ids);
                            if (ids.length > 0) {
                              loadProductsForBin(ids[0]);
                            } else {
                              setProductOptions([]);
                              setSelectedProductValues([]);
                            }
                          }}
                          style={{ width: '100%', marginBottom: 12 }}
                          allowClear
                        />
                        {productOptions.length > 0 && (
                          <>
                            <label className={style.Label} style={{ marginTop: 8 }}>Select Products (Optional)</label>
                            <Select
                              mode="multiple"
                              placeholder="Select products"
                              loading={dropdownFieldLoading}
                              options={productOptions}
                              value={selectedProductValues}
                              onChange={(values: string[]) => {
                                // Store products separately - will create a separate filter entry
                                setSelectedProductValues(values);
                              }}
                              style={{ width: '100%' }}
                              allowClear
                            />
                          </>
                        )}
                      </>
                    )}

                    {/* Product multi-select independently (no BIN required) */}
                    {activeDropdownField === 'product' && (
                      <>
                        <label className={style.Label}>Select Products</label>
                        <Select
                          mode="multiple"
                          placeholder="Select products"
                          loading={dropdownFieldLoading}
                          options={productOptions}
                          value={fieldData.fieldArrValue}
                          onChange={(values: string[]) => {
                            setFieldData(prev => ({ ...prev, fieldArrValue: values, fieldValue: values.join(',') }));
                          }}
                          style={{ width: '100%' }}
                          allowClear
                        />
                      </>
                    )}

                    {/* Regular tag input for non-special fields */}
                    {activeDropdownField === null && (
                      <div className={style.tag}>
                        <label>Values</label>
                        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                          <Input
                            className={style.inputList}
                            placeholder={fieldData.fieldValuePlaceholder || 'Type value and press Enter or click Add'}
                            value={inputValue}
                            type={toInputType(fieldData.fieldType)}
                            onChange={(e) => setInputValue(e.target.value)}
                            onPressEnter={(e) => {
                              e.preventDefault();
                              handleInputConfirm();
                            }}
                          />
                          <AntButton
                            type="primary"
                            size="small"
                            onClick={handleInputConfirm}
                            disabled={!inputValue.trim()}
                          >
                            Add
                          </AntButton>
                        </div>
                        <span className={style.tagbox}>
                          {tags.map(tag => (
                            <Tag
                              key={tag}
                              closable
                              onClose={() => handleTagClose(tag)}
                              className={style.tagContent}
                            >
                              {tag}
                            </Tag>
                          ))}
                        </span>
                        {tags.length === 0 && (
                          <small style={{ color: '#ff4d4f', fontSize: 11 }}>
                            Please add at least one value
                          </small>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Range Input */}
                {fieldData.fieldOperation === 'between' && (
                  <div className={style.field}>
                    <FormInput
                      Label={`From ${fieldData.fieldValuePlaceholder}`}
                      classInput={style.Input}
                      className={style.inputContainer}
                      labelClass={style.Label}
                      classError={style.Error}
                      name='fieldRangeValue.value1'
                      type={toInputType(fieldData.fieldType)}
                      errors={modalErrors}
                      control={modalControl}
                    />
                    <FormInput
                      Label={`To ${fieldData.fieldValuePlaceholder}`}
                      classInput={style.Input}
                      className={style.inputContainer}
                      labelClass={style.Label}
                      classError={style.Error}
                      name='fieldRangeValue.value2'
                      type={toInputType(fieldData.fieldType)}
                      errors={modalErrors}
                      control={modalControl}
                    />
                  </div>
                )}

                <div className={style.modalFooter}>
                  <AntButton onClick={handleCancel} style={{ marginRight: 10 }} className={style.andtbtnCancel}>
                    Cancel
                  </AntButton>
                  <AntButton type='primary' htmlType='submit' className={style.andtbtn}>
                    Save Field
                  </AntButton>
                </div>
              </form>
            </Modal>
          </div>
        </div>
      </div>
    </>
  );
};

// Redux connection
const mapStateToProps = (state: any) => state;

const mapDispatchToProps = (dispatch: Dispatch) => ({
  createCampaign: (campaignData: any) => dispatch(ACTIONS.createCampaign(campaignData)),
  getUnapprovedFiles: () => dispatch(ACTIONS.getUnapprovedFiles()),
  getAllBranches: (institutionId: number) => dispatch(ACTIONS.getAllBranches(institutionId)),
  getAllBins: () => dispatch(ACTIONS.getAllBins()),
  getProductsByBin: (binId: number) => dispatch(ACTIONS.getProductsByBin(binId)),
  getReportTypes: () => dispatch(ACTIONS.getReportTypes()),
});

export default connect(mapStateToProps, mapDispatchToProps)(PersonalInfoForm);


